import { useState } from "react";
import { Eye, EyeOff } from "lucide-react"; 
import "./TextBox.css";

/**
 * TextBox component renders a labeled input field with customizable properties.
 *
 * @param {Object} props
 * @param {string} props.id - The unique identifier for the input, also linked to the label's htmlFor attribute.
 * @param {string} props.label - The text label displayed above the input field.
 * @param {string} [props.type="text"] - The input type (e.g., text, email, password).
 * @param {string} [props.placeholder=""] - Placeholder text displayed inside the input when empty.
 * @param {string|number} props.value - The current value of the input field.
 * @param {function} props.onChange - Event handler function called when input value changes.
 * @param {boolean} [props.required=false] - Whether the input field is required for form submission.
 * @param {string} [props.autoComplete=""] - The autocomplete attribute for browser autofill behavior.
 *
 * @returns {JSX.Element} A labeled input wrapped in a container div.
 */
export default function TextBox({
  id,
  label,
  type = "text",
  placeholder = "",
  value,
  onChange,
  required = false,
  autoComplete = "",
}) {
  const [showPassword, setShowPassword] = useState(false);

  // Only toggle if it's a password field
  const inputType =
    type === "password" ? (showPassword ? "text" : "password") : type;

  return (
    <div className="textbox-container">
      <label htmlFor={id}>{label}</label>

      <div className="textbox-input-wrapper">
        <input
          id={id}
          type={inputType}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          required={required}
          autoComplete={autoComplete}
        />

        {/* Show toggle button only for password type */}
        {type === "password" && (
          <button
            type="button"
            className="toggle-btn"
            onClick={() => setShowPassword((prev) => !prev)}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        )}
      </div>
    </div>
  );
}